import MainClass, { MAIN_CLASS_PARAMS } from "../utils/mainClass";

import { _animate } from "../utils/_animate";
import { _cursorBubble } from "../utils/_cursorBubble";
import { _event } from "../utils/_event";
import { _lazyLoad } from "../utils/_lazyLoad";
import { _lightbox } from "../utils/_lightbox";
import { _section } from "../utils/_section";
import gsap from "gsap";
import { renderProject } from "./renderProject";

class OurWorksListing extends MainClass {
    el: {
        main: HTMLElement | null;
        filter: HTMLElement | null;
        error: HTMLElement | null;
    };

    cursorBubble: _cursorBubble | null;
    popup: _lightbox | null;

    filterChange: boolean;
    filterDefaults: { target: HTMLElement; value: string | boolean }[];

    constructor(props: MAIN_CLASS_PARAMS) {
        super(props);

        this.el = {
            main: this.selector.querySelector(".js-our-works-listing-main"),
            filter: this.selector.querySelector(".js-our-works-listing-filter"),
            error: this.selector.querySelector(".js-our-works-listing-error"),
        };

        this.cursorBubble = null;
        this.popup = null;

        this.filterChange = false;
        this.filterDefaults = [];

        this.init();
    }

    async init() {
        await this.renderCategory(); // for filters

        // get projects
        const data = await this.fetchProjects([]);
        this.renderProjects(data);

        // for intro animation
        new _animate();

        // for lazyload
        _lazyLoad();

        // for cursor bubble
        if (!_section.isMobile()) {
            this.cursorBubble = new _cursorBubble({
                target: this.selector,
                events: {
                    mousemove: (instance, event) => {
                        // TODO: Pre-deployment essential
                        // const _inItem = this.el.main.contains(event.target) && event.target.tagName.toLowerCase() === "a";
                        const _inItem = this.el.main.contains(event.target);

                        // TODO: Pre-deployment essential
                        // instance.el.text.innerHTML = _inItem ? event.target.innerText || "View" : "";
                        instance.el.text.innerHTML = _inItem ? "Coming soon" : "";

                        gsap.to(instance.el.text, {
                            duration: 0.3,
                            scale: _inItem ? 1 : 0,
                            opacity: _inItem ? 1 : 0,
                        });

                        gsap.to(instance.el.bg, {
                            duration: 0.3,
                            width: _inItem ? 104 : 12,
                            height: _inItem ? 104 : 12,
                            scale: _inItem ? 1 : 0,
                        });
                    },
                },
            });
        }
    }

    async renderCategory() {
        const data = await this.fetchCategory();
        if (!this.el.filter || !data || !data.length) return;
        const container = this.el.filter.querySelector(".js-our-works-listing-filter-list");
        if (!container) return;

        const html = data
            .map((group, groupIndex) => {
                return `
					<div class="flex flex-col gap-6">
						<div>
							<h4 class="font-sans text-p2-semibold">${group.name}</h4>
						</div>
						<div class="flex flex-wrap gap-2">
							${group.children
                                .map((child, childIndex) => {
                                    const id = `checkbox-${groupIndex}-${childIndex}`;
                                    return `
										<label class="font-sans text-p3 cursor-pointer">
											<input
												id="${id}"
												class="peer !hidden"
												type="checkbox"
												name="${child.slug}"
												value=""
												data-taxonomy="service_category"
											/>
											<span class="block px-4 md:px-6 py-3 bg-white hover:bg-neutral-10 peer-checked:bg-neutral-20 border border-neutral-30 peer-checked:border-neutral-60 rounded-lg transition duration-200">
												${child.name}
											</span>
										</label>
									`;
                                })
                                .join("")}
						</div>
					</div>
				`;
            })
            .join("");
        container.innerHTML = html;

        this.filter();
    }

    async fetchCategory() {
        const res = await fetch("/wp-json/custom/v1/service-categories");
        return await res.json();
    }

    async fetchProjects(slugs?: string[]) {
        const url = new URL("/wp-json/custom/v1/projects", window.location.origin);
        if (slugs.length > 0) {
            url.searchParams.set("service_category", slugs.join(","));
        }
        const res = await fetch(url.toString());
        return await res.json();
    }

    renderProjects(data: any[]) {
        if (!this.el.main) return;

        this.el.error?.classList.toggle("hidden", data.length > 0);

        const html = data.map((item, index) => renderProject(item, index)).join("");
        this.el.main.innerHTML = html;
        _lazyLoad();
    }

    filter() {
        if (!this.el.filter) return;
        const _inputs = this.el.filter.querySelectorAll("input");
        const _btns = {
            popup: this.selector.querySelectorAll(".js-our-works-listing-btn-filter"),
            reset: this.selector.querySelectorAll(".js-our-works-listing-btn-reset"),
            clear: this.el.filter.querySelectorAll(".js-our-works-listing-filter-btn-clear"),
            submit: this.el.filter.querySelectorAll(".js-our-works-listing-filter-btn-submit"),
        };

        // get input current state
        const _getFilterDefaults = () => {
            this.filterDefaults = [];
            _inputs.forEach((item) => {
                this.filterDefaults.push({ target: item, value: item.checked });
            });
        };
        _getFilterDefaults();

        // get projects
        const _getProjects = async () => {
            _getFilterDefaults();

            const slugs = this.filterDefaults
                .filter((item) => item.value === true)
                .map((item) => item.target.getAttribute("name"))
                .filter(Boolean);

            const data = await this.fetchProjects(slugs);
            this.renderProjects(data);
        };

        // reset filter
        const _reset = () => {
            // disable reset/all btns
            _btns.reset.forEach((item) => {
                // item.setAttribute("disabled", "");
                item.classList.add("pointer-events-none");
            });

            _inputs.forEach((item) => {
                item.checked = false;
            });

            this.selector.classList.remove("filter-change");

            this.filterChange = false;

            _getProjects();
        };

        // submit form
        const _submit = () => {
            _getProjects();

            if (this.popup)
                this.popup.hide("", () => {
                    this.filterChange = true;
                });
        };

        // recheck filter
        const _recheckFilter = (status?: string) => {
            let _checked = false;
            let _defaultValueChanged = false;

            // reset, if made changes to the filter but didnt submit
            if (status === "reset") {
                _inputs.forEach((item, index) => {
                    item.checked = this.filterDefaults[index].value ? true : false;
                });
            }

            _inputs.forEach((item, index) => {
                if (item.checked) _checked = true;
                if (item.checked !== this.filterDefaults[index].value) _defaultValueChanged = true;
            });

            // for init
            if (status === "init" && _checked) {
                this.filterChange = true;
                _defaultValueChanged = true;
            }

            if (_checked) {
                if (this.filterChange) this.selector.classList.add("filter-change");
            } else {
                this.selector.classList.remove("filter-change");
            }

            // disable reset/all btns
            _btns.reset.forEach((item) => {
                if (_checked) {
                    // item.removeAttribute("disabled");
                    item.classList.remove("pointer-events-none");
                } else {
                    // item.setAttribute("disabled", "");
                    item.classList.add("pointer-events-none");
                }
            });

            _btns.submit.forEach((item) => {
                if (_defaultValueChanged) item.removeAttribute("disabled");
                else item.setAttribute("disabled", "");
            });

            _btns.clear.forEach((item) => {
                if (_checked) item.removeAttribute("disabled");
                else item.setAttribute("disabled", "");
            });
        };
        _recheckFilter("init");

        // for inputs filter change
        _inputs.forEach((item) => {
            item.addEventListener("change", () => {
                _recheckFilter();
            });
        });

        // for reset btn
        _btns.reset.forEach((item) => {
            // item.setAttribute("disabled", "");
            item.classList.add("pointer-events-none");
            item.addEventListener("click", (e) => {
                e.preventDefault();

                _reset();
            });
        });

        // for submit btn
        _btns.submit.forEach((item) => {
            item.setAttribute("disabled", "");
            item.addEventListener("click", (e) => {
                e.preventDefault();

                _submit();
            });
        });

        // for clear btn
        _btns.clear.forEach((item) => {
            item.addEventListener("click", () => {
                if (item.getAttribute("disabled")) return;

                _inputs.forEach((input) => {
                    if (!input.checked) return;
                    input.checked = false;
                    _event.dispatch(input, "change");
                });
            });
        });

        // for popup btn
        _btns.popup.forEach((item) => {
            item.addEventListener("click", (e) => {
                e.preventDefault();

                // open popup filter
                this.popup = new _lightbox({
                    target: this.el.filter,
                    events: {
                        beforeShow: (instance) => {
                            instance.content.original.prepend(instance.el.btn.close);
                            const _span = document.createElement("span");
                            _span.innerText = "Close";
                            instance.el.btn.close.appendChild(_span);
                            instance.el.btn.close.classList.remove("fixed", "text-white", "hover:text-red-500");
                            instance.el.btn.close.classList.add(
                                "absolute",
                                "flex",
                                "items-center",
                                "gap-1",
                                "!p-4",
                                "!pb-2",
                                "md:!px-10",
                                "md:!py-6",
                                "md:!pb-4",
                                "font-sans",
                                "text-sm",
                                "text-neutral-60",
                                "[&_svg]:w-4",
                            );

                            _getFilterDefaults();
                            _recheckFilter();
                        },
                        beforeClose: () => {
                            _recheckFilter(!this.filterChange ? "reset" : "");
                        },
                    },
                });
            });
        });
    }
}

export default OurWorksListing;
